/**
 * CanvasContextMenu — the right-click menu shown on the canvas and on nodes.
 *
 * Visuals follow Figma "Canvas UI 2.0" node 566:1022 ("Right click on canvas" /
 * "Right click on node"): a 233px #202020 panel with a #4C4C4C 1px border and
 * 16px radius, 8px padding and gap, 37px rows with 12px radius and 10px
 * padding, Figtree Medium 15/20 white labels, and full-bleed #4C4C4C 1px
 * dividers between sections. The #4C4C4C row fill is the hover / keyboard
 * focus highlight.
 *
 * Rendered in a portal at the pointer, nudged back inside the viewport. Closes
 * on Escape, an outside pointer-down, scroll/wheel, resize or window blur.
 * Arrow keys / Home / End move focus between enabled rows; Enter or Space picks.
 */

import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import type { ContextMenuKind } from '../../../analytics/generated/analyticsEvents'
import type { ContextMenuItemId, ContextMenuSection } from './contextMenuModel'

/** Gap kept between the menu and the viewport edge when it is nudged inside. */
const VIEWPORT_MARGIN_PX = 8

export interface CanvasContextMenuProps {
  kind: ContextMenuKind
  /** Pointer position in client (viewport) coordinates. */
  x: number
  y: number
  sections: ContextMenuSection[]
  onSelect: (id: ContextMenuItemId) => void
  onClose: (reason: string) => void
}

export function CanvasContextMenu({ kind, x, y, sections, onSelect, onClose }: CanvasContextMenuProps) {
  const { t } = useTranslation('canvas')
  const menuRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ left: x, top: y })

  // Measure once mounted and keep the whole menu on screen.
  useLayoutEffect(() => {
    const el = menuRef.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX
    const maxTop = window.innerHeight - height - VIEWPORT_MARGIN_PX
    setPosition({
      left: Math.max(VIEWPORT_MARGIN_PX, Math.min(x, maxLeft)),
      top: Math.max(VIEWPORT_MARGIN_PX, Math.min(y, maxTop)),
    })
  }, [x, y, sections])

  useEffect(() => {
    menuRef.current?.focus({ preventScroll: true })
  }, [x, y, kind])

  useEffect(() => {
    const isInside = (target: EventTarget | null) =>
      target instanceof Node && !!menuRef.current?.contains(target)
    const onPointerDown = (e: PointerEvent) => {
      if (!isInside(e.target)) onClose('outside-pointer')
    }
    const onWheel = (e: WheelEvent) => {
      if (!isInside(e.target)) onClose('wheel')
    }
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose('escape')
      }
    }
    const onResize = () => onClose('resize')
    const onBlur = () => onClose('window-blur')
    window.addEventListener('pointerdown', onPointerDown, true)
    window.addEventListener('wheel', onWheel, { capture: true, passive: true })
    window.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true)
      window.removeEventListener('wheel', onWheel, true)
      window.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('blur', onBlur)
    }
  }, [onClose])

  const focusItem = useCallback((direction: 1 | -1 | 'first' | 'last') => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? [],
    )
    if (items.length === 0) return
    const current = items.findIndex((el) => el === document.activeElement)
    let next: number
    if (direction === 'first') next = 0
    else if (direction === 'last') next = items.length - 1
    else if (current === -1) next = direction === 1 ? 0 : items.length - 1
    else next = (current + direction + items.length) % items.length
    items[next].focus()
  }, [])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    // Keep menu navigation away from the canvas-wide shortcuts (Delete, Cmd+C, Tab…).
    e.stopPropagation()
    if (e.key === 'ArrowDown') { e.preventDefault(); focusItem(1) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusItem(-1) }
    else if (e.key === 'Home') { e.preventDefault(); focusItem('first') }
    else if (e.key === 'End') { e.preventDefault(); focusItem('last') }
    else if (e.key === 'Tab') { e.preventDefault(); focusItem(e.shiftKey ? -1 : 1) }
  }, [focusItem])

  const visibleSections = sections.filter((section) => section.length > 0)

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      aria-label={t(`canvasContextMenu.label.${kind}`)}
      data-testid="canvas-context-menu"
      data-menu-kind={kind}
      onKeyDown={handleKeyDown}
      onContextMenu={(e) => e.preventDefault()}
      style={{ left: position.left, top: position.top }}
      className={cn(
        'fixed z-50 flex w-[233px] flex-col gap-2 p-2 outline-none',
        'rounded-2xl border border-context-menu-border bg-context-menu',
        'font-figtree text-menu font-medium text-white',
      )}
    >
      {visibleSections.map((section, sectionIndex) => (
        <Fragment key={section.map((item) => item.id).join('|')}>
          {sectionIndex > 0 && (
            <div
              role="separator"
              className="-mx-1 h-px shrink-0 bg-context-menu-border"
              data-testid="canvas-context-menu-separator"
            />
          )}
          {section.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              aria-disabled={item.disabled}
              data-testid={`canvas-context-menu-item-${item.id}`}
              onClick={() => onSelect(item.id)}
              onPointerEnter={(e) => { if (!item.disabled) e.currentTarget.focus({ preventScroll: true }) }}
              className={cn(
                'flex h-[37px] w-full shrink-0 items-center whitespace-nowrap rounded-xl p-2.5 text-left outline-none',
                'transition-colors duration-100',
                item.disabled
                  ? 'cursor-default opacity-40'
                  : 'hover:bg-context-menu-highlight focus-visible:bg-context-menu-highlight focus:bg-context-menu-highlight',
              )}
            >
              {t(`canvasContextMenu.items.${item.id}`)}
            </button>
          ))}
        </Fragment>
      ))}
    </div>,
    document.body,
  )
}
